import React, { useEffect, useState, useMemo } from "react";
import { Input, Layout, Menu, Space, Drawer, Select, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { MenuOutlined, UserOutlined } from "@ant-design/icons";
import "./css/Header.css";
import { useTranslation } from "react-i18next";
import { groupedTools } from "../data/toolsData";
import { forceLogin } from "../hooks/forceLogin";
import { setSearch } from '../features/categorySlice';
import { useDispatch, useSelector } from 'react-redux';
import StandardButton from '../components/StandardButton';
import { localizedPath } from '../utils/paths';


const tools = Object.entries(groupedTools).map(([category, items]) => ({
  category,
  items,
}));


const Header = ({ customHeading = null, handleActiveCatgory }) => {
  forceLogin();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchText = useSelector((state) => state.category.searchText);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const checkLoginStatus = () => {
      const storedUser = localStorage.getItem("userProfileData");
      setIsLoggedIn(!!storedUser);
    };

    checkLoginStatus();

    // Listen for storage changes (for logout/login from other tabs)
    const handleStorageChange = () => {
      checkLoginStatus();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const allTools = Object.values(groupedTools).flat();
  const currentPath = location.pathname.replace(/^\/(en|es|zh-CN|de|fr|ja)(?=\/|$)/, "").replace(/\/+$/, '');
  const currentTool = allTools.find((tool) =>
    tool.path === currentPath
  );

  const headingKey = customHeading
    ? customHeading
    : currentTool
      ? currentTool.nameKey
      : "home_site_title";

  const descriptionKey = currentTool?.descriptionKey;

  const TOOL_USAGE_KEY = "tool_usage";

  const getToolUsage = () => {
    const stored = localStorage.getItem(TOOL_USAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  };

  const incrementToolUsage = (toolName) => {
    const usage = getToolUsage();
    usage[toolName] = (usage[toolName] || 0) + 1;
    localStorage.setItem(TOOL_USAGE_KEY, JSON.stringify(usage));
    dispatch(setSearch(toolName))
  };

  const getMenuTools = () => {
    const allMenus = [];
    tools?.map((toolsItem, categoryIndex) => {
      allMenus.push({
        label: toolsItem.category,
        title: toolsItem.category,
        options: toolsItem.items.map((toolMenuItem, toolMenuIndex) => {
          return { label: <Link key={`${categoryIndex}_${toolMenuItem.name}_${toolMenuIndex}`} to={localizedPath(i18n.language, toolMenuItem.path)} onClick={() => incrementToolUsage(toolMenuItem.name)}><Space><img src={toolMenuItem.icon} alt={t(toolMenuItem.nameKey)} className="w-5" loading="lazy" />{t(toolMenuItem.nameKey)}</Space></Link>, title: t(toolMenuItem.nameKey), key: `${toolsItem.category}_${toolMenuItem.nameKey}_${toolMenuIndex}` }
        }),
        key: `${toolsItem.category}_${categoryIndex}`
      })
    })
    return allMenus;
  }

  const menuItems = [
    { key: "AI", label: t("ai") },
    { key: "URL", label: t("url") },
    { key: "TEXT", label: t("text") },
    { key: "PDF", label: t("pdf") }
  ];

  const filterMenu = (items, term) => {
    if (!term) return items;

    return items.filter(item => {
      const labelMatch = item.title.toLowerCase().includes(term.toLowerCase());
      if (labelMatch) return true;

      if (item.options) {
        const filteredChildren = filterMenu(item.options, term);
        return filteredChildren.length > 0;
      }
      return false;
    }).map(item => {
      if (item.options) {
        return { ...item, options: filterMenu(item.options, term) };
      }
      return item;
    });
  };

  const filteredItems = useMemo(() => filterMenu(getMenuTools(), searchTerm), [searchTerm]);

  const navigateToHome = () => {
    navigate(localizedPath(i18n.language));
    dispatch(setSearch(''))
  }

  const handleMenuClick = (e) => {
    if (typeof handleActiveCatgory === 'undefined') {
      navigateToHome();
    } else {
      handleActiveCatgory(e.key)
    }
  }

  return (
    <>
      <Layout.Header className="custom-header">
        {/* Logo */}
        <div className="logo" onClick={navigateToHome}>
          <Link>
            <img src="/icon.png" alt="Logo" />
          </Link>
        </div>

        {/* Desktop Menu */}
        <Menu
          mode="horizontal"
          className="menu desktop-menu"
          items={menuItems}
          onClick={handleMenuClick}
        />

        {/* Right Actions */}
        <Space className="header-actions">
          <Select
            showSearch
            style={{ width: 300 }}
            placeholder={t("home_search_placeholder")}
            onSearch={(value) => setSearchTerm(value)}
            filterOption={false}
            key={'Search-box'}
            allowClear
            options={filteredItems}
            value={searchText}
            onClear={() => {
              dispatch(setSearch(''))
            }}
          />

          {!isLoggedIn && (
            <Link to={localizedPath(i18n.language, "/auth/login")}>
              <StandardButton type="primary" className="signup-btn">
                {t('login_button')}
              </StandardButton>
            </Link>
          )}
          {isLoggedIn && (
            <Link to={localizedPath(i18n.language, "/auth/login")}>
              <StandardButton
                type="secondary"
                variant="outlined"
                className="profile-btn flex items-center gap-2"
                icon={<UserOutlined />}
              >
                {t('profile_button')}
              </StandardButton>
            </Link>
          )}
          <select
            onChange={(e) => {
              const newLang = e.target.value;
              i18n.changeLanguage(newLang);
              const currentPath = window.location.pathname
                .replace(/^\/(en|es|zh-CN|de|fr|ja)(?=\/|$)/, '')
                .replace(/\/+$/, '');
              window.location.pathname = localizedPath(newLang, currentPath);
            }}
            value={i18n.language}
            className="border border-indigo-300 text-indigo-700 rounded px-3 py-1 text-sm"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="zh-CN">中文（简体）</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
            <option value="ja">日本語</option>
          </select>
        </Space>


</Layout.Header>

      {/* Spacer to prevent layout jump */}
      <div className="header-spacer" />

      {descriptionKey && (
        <div className="tool-title-bar">
          <h1>{t(headingKey)}</h1>
          <Tooltip title={t(descriptionKey)} placement="bottom" styles={{ root: { maxWidth: 400 } }}>
            <InfoCircleOutlined className="tool-title-info" />
          </Tooltip>
        </div>
      )}
    </>
  );
};

export default Header;